import 'simplebar-react/dist/simplebar.min.css'

import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useEffect, useMemo, useState } from 'react'
import { BiSolidMessageRoundedAdd } from 'react-icons/bi'
import { FiBook, FiGrid, FiMoreHorizontal, FiUser, FiX } from 'react-icons/fi'
import { LuMessageSquareMore } from 'react-icons/lu'
import { LuBot } from 'react-icons/lu'
import { LuArrowUpToLine } from 'react-icons/lu'
import { PiPlanetBold } from 'react-icons/pi'
import { TbAlertCircle, TbEdit, TbLogout, TbPinned, TbPinnedOff, TbStar, TbStarOff, TbTrash } from 'react-icons/tb'
import { useLocation, useNavigate, useParams } from 'react-router'
import SimpleBar from 'simplebar-react'
import { toast } from 'sonner'

import { deleteSession, favoriteSession, pinSession } from '@/api/agent'
import logo from '@/assets/images/logo.png'
import { useChatStore, useUserStore } from '@/store'

import RenameModal from './RenameModal'
import ReportModal from './ReportModal'

function SessionIcon({ icon, isActive }: { icon?: string; isActive: boolean }) {
  const [iconLoadFailed, setIconLoadFailed] = useState(false)
  const iconText = icon?.trim()
  const isImageIcon = !!iconText && /^(https?:\/\/|\/|data:image\/)/i.test(iconText)

  if (isImageIcon && !iconLoadFailed) {
    return <img src={iconText} alt="" className="h-6 w-6 shrink-0 rounded-sm object-cover" onError={() => setIconLoadFailed(true)} />
  }

  if (iconText && !isImageIcon) {
    return <span className="h-4 w-4 shrink-0 text-center text-sm leading-4">{iconText}</span>
  }

  return <LuMessageSquareMore className={`h-4 w-4 shrink-0 ${isActive ? 'text-gray-500' : 'text-gray-400'}`} />
}

export default function Sidebar() {
  const { isSidebarOpen, setIsSidebarOpen, setIsLoginModalOpen, sessionList, fetchSessions, clearSessions, setIsResourcePreviewOpen, setIsHtmlPreviewOpen, triggerNewChat } = useChatStore()
  const user = useUserStore((state) => state.user)
  const navigate = useNavigate()
  const { sessionId } = useParams<{ sessionId?: string }>()
  const location = useLocation()

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)

  const [activeChatMenu, setActiveChatMenu] = useState<string | null>(null)

  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const [reportSessionId, setReportSessionId] = useState<string | null>(null)

  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false)
  const [renameSessionId, setRenameSessionId] = useState<string | null>(null)
  const [renameInitialTitle, setRenameInitialTitle] = useState('')
  const portalContainer = useMemo(() => (typeof document !== 'undefined' ? document.body : undefined), [])

  useEffect(() => {
    if (user) {
      fetchSessions()
    } else {
      clearSessions()
    }
  }, [user, fetchSessions, clearSessions])

  const handleDeleteSession = async (targetSessionId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await deleteSession(targetSessionId)
      setActiveChatMenu(null)
      fetchSessions()
      if (sessionId === targetSessionId) {
        triggerNewChat()
        navigate('/chat')
      }
    } catch (error) {
      console.error('Failed to delete session', error)
    }
  }

  const handlePinToggle = async (targetSessionId: string, currentPinned: boolean, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await pinSession(targetSessionId, !currentPinned)
      setActiveChatMenu(null)
      fetchSessions()
    } catch (error) {
      console.error('Failed to toggle pin status', error)
    }
  }

  const handleFavoriteToggle = async (targetSessionId: string, currentFavorited: boolean, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await favoriteSession(targetSessionId, !currentFavorited)
      setActiveChatMenu(null)
      fetchSessions()
      if (!currentFavorited) {
        toast.success('收藏成功')
      } else {
        toast.success('取消收藏成功')
      }
    } catch (error) {
      console.error('Failed to toggle favorite status', error)
    }
  }

  const handleNavClick = (path: string) => {
    const isChatConversationRoute = path === '/chat' || sessionList.some((session) => `/chat/${session.sessionId}` === path)
    if (isChatConversationRoute) {
      setIsResourcePreviewOpen(false)
      setIsHtmlPreviewOpen(false)
    }
    if (path === '/chat') {
      triggerNewChat()
    }
    navigate(path)
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false)
    }
  }

  return (
    <>
      {/* Mobile Overlay Backdrop */}
      {isSidebarOpen && <div className="fixed inset-0 z-40 bg-black/20 tablet:hidden" onClick={() => setIsSidebarOpen(false)} />}

      {/* Sidebar Container */}
      <div
        className={`
          fixed inset-y-0 left-0 z-50 h-full border-r border-gray-100 bg-gray-50/50 transition-all duration-300 ease-in-out overflow-hidden
          tablet:relative
          ${isSidebarOpen ? 'translate-x-0 w-64' : '-translate-x-full w-64 tablet:translate-x-0 tablet:w-0'}
        `}
      >
        <div className="flex h-full w-64 flex-col bg-white">
          {/* Mobile Close Button */}
          <button className="absolute right-3 top-5 p-1 text-gray-500 hover:bg-gray-200 rounded-md tablet:hidden" onClick={() => setIsSidebarOpen(false)}>
            <FiX className="h-5 w-5" />
          </button>

          {/* Logo Area */}
          <div className="flex justify-start items-start gap-2 p-5">
            <div className="flex h-8 items-center justify-start overflow-hidden rounded-md bg-white">
              <img src={logo} alt="神笔马良AI Logo" className="h-full w-full object-contain object-left" />
            </div>
          </div>

          {/* New Chat Button */}
          <div className="px-4 pb-4">
            <button
              onClick={() => handleNavClick('/chat')}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-blue-50 py-3 text-blue-600 font-medium md:hover:bg-blue-100 active:bg-blue-100 transition-colors"
            >
              <BiSolidMessageRoundedAdd className="h-5 w-5" />
              <span className="font-bold text-[rgba(0,0,0,0.87)]">开启新对话</span>
            </button>
          </div>

          {/* 分割线 */}
          <div className="px-4 mb-2">
            <div className="w-full border-t border-dashed border-[#DADADA]"></div>
          </div>

          {/* Nav Menu */}
          <div className="flex flex-col gap-1 px-4 py-2">
            <button
              onClick={() => handleNavClick('/chat/skills')}
              className={`flex items-center gap-3 rounded-lg px-3 py-3 transition-colors ${location.pathname === '/chat/skills' ? 'bg-blue-600 text-white' : 'text-[rgba(0,0,0,0.87)] md:hover:bg-gray-100 active:bg-gray-50'}`}
            >
              <FiGrid className="h-4 w-4" />
              <span>技能广场</span>
            </button>
            {/* <button
              onClick={() => handleNavClick('/chat/bot/discover')}
              className={`flex items-center gap-3 rounded-lg px-3 py-3 transition-colors ${location.pathname.startsWith('/chat/bot') ? 'bg-blue-600 text-white' : 'text-[rgba(0,0,0,0.87)] md:hover:bg-gray-100 active:bg-gray-50'}`}
            >
              <LuBot className="h-4 w-4" />
              <span>AI智能体</span>
            </button>
            <button
              onClick={() => handleNavClick('/chat/course')}
              className={`flex items-center gap-3 rounded-lg px-3 py-3 transition-colors ${location.pathname === '/chat/course' ? 'bg-blue-600 text-white' : 'text-[rgba(0,0,0,0.87)] md:hover:bg-gray-100 active:bg-gray-50'}`}
            >
              <FiBook className="h-4 w-4" />
              <span>超级课件</span>
            </button>
            <button
              onClick={() => handleNavClick('/chat/forum')}
              className={`flex items-center gap-3 rounded-lg px-3 py-3 transition-colors ${location.pathname === '/chat/forum' ? 'bg-blue-600 text-white' : 'text-[rgba(0,0,0,0.87)] md:hover:bg-gray-100 active:bg-gray-50'}`}
            >
              <PiPlanetBold className="h-4 w-4" />
              <span>创作社区</span>
            </button> */}
          </div>

          {/* History Area */}
          <div className="mt-4 flex flex-1 flex-col overflow-hidden pb-2">
            <div className="mb-2 text-xs font-semibold text-gray-400 shrink-0 px-6">历史对话</div>
            <SimpleBar className="flex-1 w-full" style={{ height: 0 }}>
              <div className="flex flex-col gap-1 relative px-3">
                {sessionList.map((chat) => {
                  const id = chat.sessionId
                  const title = chat.title || '新对话'
                  const isActive = id === sessionId
                  return (
                    <div key={id} className="relative group">
                      <button
                        onClick={() => handleNavClick(`/chat/${id}`)}
                        className={`flex w-full items-center gap-1 rounded-lg pl-3 pr-8 py-2.5 text-left transition-colors ${isActive ? 'bg-gray-100 text-[rgba(0,0,0,0.87)] font-bold' : 'text-[rgba(0,0,0,0.87)] md:hover:bg-gray-100 active:bg-gray-50'}`}
                      >
                        <SessionIcon icon={chat.icon} isActive={isActive} />
                        <span className="truncate flex-1 text-sm">{title}</span>
                      </button>

                      {/* Pinned Icon (mutually exclusive with More Options Button) */}
                      {chat.topFlag && activeChatMenu !== id && (
                        <div
                          className={`absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full text-gray-400 transition-opacity pointer-events-none ${isActive ? 'opacity-0' : 'opacity-100 md:group-hover:opacity-0'}`}
                        >
                          <TbPinned className="h-4 w-4" />
                        </div>
                      )}

                      <DropdownMenu.Root
                        modal={false}
                        open={activeChatMenu === id}
                        onOpenChange={(open) => {
                          setActiveChatMenu((prev) => {
                            if (open) return id
                            return prev === id ? null : prev
                          })
                        }}
                      >
                        {/* More Options Button */}
                        <DropdownMenu.Trigger asChild>
                          <button
                            onClick={(e) => e.stopPropagation()}
                            className={`absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm text-gray-400 md:hover:text-gray-600 active:text-gray-600 transition-opacity ${
                              activeChatMenu === id || isActive ? 'opacity-100' : 'opacity-0 md:group-hover:opacity-100'
                            }`}
                          >
                            <FiMoreHorizontal className="h-4 w-4" />
                          </button>
                        </DropdownMenu.Trigger>

                        {/* Context Menu Dropdown */}
                        <DropdownMenu.Portal container={portalContainer}>
                          <DropdownMenu.Content
                            side="bottom"
                            align="end"
                            sideOffset={6}
                            className="w-32 rounded-xl border border-gray-100 bg-white p-1.5 shadow-[0_4px_20px_rgba(0,0,0,0.08)]"
                            style={{ zIndex: 2147483647 }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <button
                              onClick={(e) => handlePinToggle(id, chat.topFlag, e)}
                              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              {chat.topFlag ? <TbPinnedOff className="h-4 w-4 shrink-0 text-gray-500" /> : <LuArrowUpToLine className="h-4 w-4 shrink-0 text-gray-500" />}
                              <span>{chat.topFlag ? '取消置顶' : '置顶'}</span>
                            </button>
                            {!chat.defaultSession && (
                              <>
                                {/* <button
                                  onClick={(e) => handleFavoriteToggle(id, chat.favorited, e)}
                                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  {chat.favorited ? <TbStarOff className="h-4 w-4 shrink-0 text-gray-500" /> : <TbStar className="h-4 w-4 shrink-0 text-gray-500" />}
                                  <span>{chat.favorited ? '取消收藏' : '收藏'}</span>
                                </button> */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    setRenameSessionId(id)
                                    setRenameInitialTitle(title)
                                    setIsRenameModalOpen(true)
                                    setActiveChatMenu(null)
                                  }}
                                  className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                  <TbEdit className="h-4 w-4 shrink-0 text-gray-500" />
                                  <span>重命名</span>
                                </button>
                              </>
                            )}
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                setReportSessionId(id)
                                setIsReportModalOpen(true)
                                setActiveChatMenu(null)
                              }}
                              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                            >
                              <TbAlertCircle className="h-4 w-4 shrink-0 text-gray-500" />
                              <span>举报</span>
                            </button>
                            <div className="my-1 h-[1px] w-full bg-gray-100"></div>
                            <button
                              onClick={(e) => handleDeleteSession(id, e)}
                              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                            >
                              <TbTrash className="h-4 w-4 shrink-0 text-red-500" />
                              <span>删除</span>
                            </button>
                          </DropdownMenu.Content>
                        </DropdownMenu.Portal>
                      </DropdownMenu.Root>
                    </div>
                  )
                })}
              </div>
            </SimpleBar>
          </div>

          {/* User Profile */}
          <div className="border-t border-gray-100 p-4 relative">
            {user ? (
              <DropdownMenu.Root modal={false} open={isUserMenuOpen} onOpenChange={setIsUserMenuOpen}>
                <DropdownMenu.Trigger asChild>
                  <button className="flex w-full items-center gap-2 rounded-lg p-1 md:hover:bg-gray-100 active:bg-gray-50 transition-colors">
                    <img src={user.avatar || 'https://a1.dianping.art/images/head_v2/025.png'} alt="avatar" className="h-8 w-8 rounded-full border border-gray-200 object-cover" />
                    <span className="font-medium text-gray-700 truncate">{user.nickname || '用户'}</span>
                  </button>
                </DropdownMenu.Trigger>

                <DropdownMenu.Portal container={portalContainer}>
                  <DropdownMenu.Content
                    side="top"
                    align="start"
                    sideOffset={8}
                    className="w-[calc(16rem-2rem)] rounded-xl border border-gray-100 bg-white p-1.5 shadow-[0_4px_20px_rgba(0,0,0,0.08)]"
                    style={{ zIndex: 2147483647 }}
                  >
                    <button
                      onClick={() => {
                        window.dispatchEvent(new Event('auth:unauthorized'))
                        setIsUserMenuOpen(false)
                        if (window.innerWidth < 768) {
                          setIsSidebarOpen(false)
                        }
                      }}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-red-600 transition-colors hover:bg-red-50"
                    >
                      <TbLogout className="h-4 w-4" />
                      <span>退出登录</span>
                    </button>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            ) : (
              <button
                onClick={() => {
                  setIsLoginModalOpen(true)
                  if (window.innerWidth < 768) {
                    setIsSidebarOpen(false)
                  }
                }}
                className="flex w-full items-center gap-2 rounded-lg p-1 md:hover:bg-gray-100 active:bg-gray-50 transition-colors"
              >
                <div className="flex h-8 w-8 items-center justify-center overflow-hidden rounded-full bg-gray-200">
                  <FiUser className="h-4 w-4 text-gray-500" />
                </div>
                <span className="font-medium text-gray-700">未登录</span>
              </button>
            )}
          </div>
        </div>
      </div>

      <ReportModal
        isOpen={isReportModalOpen}
        sessionId={reportSessionId}
        onClose={() => {
          setIsReportModalOpen(false)
          setReportSessionId(null)
        }}
      />

      <RenameModal
        isOpen={isRenameModalOpen}
        sessionId={renameSessionId}
        initialTitle={renameInitialTitle}
        onClose={() => {
          setIsRenameModalOpen(false)
          setRenameSessionId(null)
        }}
      />
    </>
  )
}
